/**
 * AdminKitDocSimpleTab — generic admin tab to manage one kind of
 * "kit document" (Integração Matriz/Filial, LGPD, Ordem de Serviço).
 *
 * Pattern intentionally simpler than AdminContractsTab: one CTA to
 * generate the doc for a single employee, a list of generated docs
 * with status / actions (preview, delete) and a search filter.
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Card, CardContent } from "./ui/card";
import { Badge } from "./ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "./ui/select";
import { toast } from "sonner";
import {
  Plus, RefreshCw, Trash2, Eye, Search, Loader2, CheckCircle2, Clock,
  Upload, Building2,
} from "lucide-react";
import BulkDocsUploadDialog from "./BulkDocsUploadDialog";

const AdminKitDocSimpleTab = ({ adminToken, kind, title, description }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  // Generate dialog state
  const [open, setOpen] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [empQuery, setEmpQuery] = useState("");
  const [selectedEmp, setSelectedEmp] = useState("");
  const [carga, setCarga] = useState("6h");
  const [funcao, setFuncao] = useState("");
  const [posto, setPosto] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  // Bulk-by-contract dialog state
  const [contractOpen, setContractOpen] = useState(false);
  const [contracts, setContracts] = useState([]);
  const [selContract, setSelContract] = useState("");
  const [bulkJob, setBulkJob] = useState(null);
  const [bulkStarting, setBulkStarting] = useState(false);

  const reqHeaders = { Authorization: `Bearer ${adminToken}` };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(
        `${API}/admin/kit-documents?kind=${kind}`,
        { headers: reqHeaders, timeout: 15000 },
      );
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error("Falha ao listar documentos");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, adminToken]);

  useEffect(() => { load(); }, [load]);

  const fetchEmployees = useCallback(async () => {
    try {
      const r = await axios.get(
        `${API}/admin/employees?limit=400&search=${encodeURIComponent(empQuery)}`,
        { headers: reqHeaders, timeout: 15000 },
      );
      setEmployees(r.data?.employees || []);
    } catch (e) {
      // silent
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken, empQuery]);

  useEffect(() => {
    if (open) fetchEmployees();
  }, [open, fetchEmployees]);

  const handleGenerate = async () => {
    if (!selectedEmp) {
      toast.error("Selecione um funcionário");
      return;
    }
    setSubmitting(true);
    try {
      const extras = {};
      if (kind === "integracao_matriz" || kind === "integracao_filial") {
        extras.carga_horaria = carga;
      }
      if (kind === "ordem_servico") {
        if (funcao) extras.funcao = funcao;
        if (posto) extras.posto = posto;
      }
      await axios.post(
        `${API}/admin/kit-documents/create`,
        { kind, employee_id: selectedEmp, extras },
        { headers: { ...reqHeaders, "Content-Type": "application/json" } },
      );
      toast.success("Documento gerado — pendente de assinatura");
      setOpen(false);
      setSelectedEmp(""); setCarga("6h");
      setFuncao(""); setPosto("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao gerar");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePreview = (id) => {
    window.open(
      `${API}/admin/kit-documents/${id}/preview?admin_token=${encodeURIComponent(adminToken)}`,
      "_blank",
    );
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Excluir este documento?")) return;
    try {
      await axios.delete(`${API}/admin/kit-documents/${id}`,
        { headers: reqHeaders });
      toast.success("Removido");
      await load();
    } catch (e) {
      toast.error("Falha ao excluir");
    }
  };

  const openContractDialog = async () => {
    setContractOpen(true);
    setBulkJob(null);
    setSelContract("");
    try {
      const r = await axios.get(`${API}/admin/kit-documents/contracts`,
        { headers: reqHeaders, timeout: 30000 });
      setContracts(r.data?.items || []);
    } catch (e) {
      toast.error("Falha ao carregar contratos");
    }
  };

  const startBulk = async () => {
    setBulkStarting(true);
    try {
      const r = await axios.post(
        `${API}/admin/kit-documents/bulk-create`,
        { kind, contract: selContract },
        { headers: { ...reqHeaders, "Content-Type": "application/json" } },
      );
      setBulkJob({ job_id: r.data.job_id, total: r.data.total,
        done: 0, skipped: 0, errors: 0, status: "running" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao iniciar emissão");
    } finally {
      setBulkStarting(false);
    }
  };

  useEffect(() => {
    if (!bulkJob?.job_id || bulkJob.status !== "running") return;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(
          `${API}/admin/kit-documents/bulk-jobs/${bulkJob.job_id}`,
          { headers: reqHeaders });
        setBulkJob((prev) => ({ ...prev, ...r.data }));
        if (r.data.status !== "running") {
          clearInterval(t);
          load();
          toast.success(
            `Emissão concluída: ${r.data.done} emitido(s), ${r.data.skipped} já tinham`);
        }
      } catch (e) { /* keep polling */ }
    }, 1500);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkJob?.job_id, bulkJob?.status]);

  const filtered = items.filter((it) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (it.employee_nome || "").toLowerCase().includes(q) ||
      (it.employee_matricula || "").includes(q)
    );
  });

  const showCarga = (kind === "integracao_matriz"
                     || kind === "integracao_filial");
  const showFuncaoPosto = (kind === "ordem_servico");

  return (
    <div className="space-y-4" data-testid={`kit-tab-${kind}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h3 className="text-base font-semibold text-slate-900">{title}</h3>
          {description && (
            <p className="text-xs text-slate-500 mt-0.5">{description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}
                  disabled={loading} data-testid={`refresh-${kind}`}>
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} />
            Atualizar
          </Button>
          <Button variant="outline" size="sm" onClick={() => setBulkOpen(true)}
                  data-testid={`bulk-import-${kind}`}>
            <Upload className="w-4 h-4 mr-1.5" />
            Importar PDF
          </Button>
          <Button variant="outline" size="sm" onClick={openContractDialog}
                  className="text-indigo-700 border-indigo-200 hover:bg-indigo-50"
                  data-testid={`bulk-contract-${kind}`}>
            <Building2 className="w-4 h-4 mr-1.5" />
            Emitir por Contrato
          </Button>
          <Button size="sm" onClick={() => setOpen(true)}
                  data-testid={`generate-${kind}`}>
            <Plus className="w-4 h-4 mr-1.5" />
            Gerar para Funcionário
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
        <Input
          placeholder="Buscar por nome ou matrícula..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid={`search-${kind}`}
        />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-md border border-slate-200 bg-white p-3">
          <div className="text-[10px] text-slate-500 uppercase font-bold">Total</div>
          <div className="text-2xl font-bold text-slate-900"
               data-testid={`kpi-total-${kind}`}>
            {items.length}
          </div>
        </div>
        <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
          <div className="text-[10px] text-amber-700 uppercase font-bold">Pendentes</div>
          <div className="text-2xl font-bold text-amber-900"
               data-testid={`kpi-pending-${kind}`}>
            {items.filter((it) => it.status !== "signed").length}
          </div>
        </div>
        <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3">
          <div className="text-[10px] text-emerald-700 uppercase font-bold">Assinados</div>
          <div className="text-2xl font-bold text-emerald-900"
               data-testid={`kpi-signed-${kind}`}>
            {items.filter((it) => it.status === "signed").length}
          </div>
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          {loading && items.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
              Carregando…
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              Nenhum documento gerado ainda.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs uppercase text-slate-600">
                  <th className="px-4 py-2.5">Funcionário</th>
                  <th className="px-4 py-2.5">Matrícula</th>
                  <th className="px-4 py-2.5">Status</th>
                  <th className="px-4 py-2.5">Gerado em</th>
                  <th className="px-4 py-2.5 w-32 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it) => (
                  <tr key={it.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2.5 font-medium text-slate-900">
                      {it.employee_nome || "—"}
                    </td>
                    <td className="px-4 py-2.5 text-slate-600">
                      {it.employee_matricula || "—"}
                    </td>
                    <td className="px-4 py-2.5">
                      {it.status === "signed" ? (
                        <Badge variant="outline"
                               className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Assinado
                        </Badge>
                      ) : (
                        <Badge variant="outline"
                               className="bg-amber-50 text-amber-700 border-amber-200">
                          <Clock className="w-3 h-3 mr-1" />
                          Pendente
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500">
                      {it.created_at
                        ? new Date(it.created_at).toLocaleString("pt-BR")
                        : "—"}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      <Button variant="ghost" size="sm"
                              onClick={() => handlePreview(it.id)}
                              data-testid={`preview-${it.id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm"
                              onClick={() => handleDelete(it.id)}
                              data-testid={`delete-${it.id}`}>
                        <Trash2 className="w-4 h-4 text-rose-600" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={contractOpen} onOpenChange={(o) => {
        if (!o && bulkJob?.status === "running") return; // não fechar no meio
        setContractOpen(o);
      }}>
        <DialogContent className="max-w-md" data-testid={`contract-dialog-${kind}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              Emitir {title} por contrato
            </DialogTitle>
          </DialogHeader>
          {!bulkJob ? (
            <div className="space-y-3">
              <p className="text-xs text-slate-500">
                O documento será gerado para TODOS os funcionários do contrato
                e ficará como pendência obrigatória no portal (sem notificação).
                Quem já tem este documento será pulado.
              </p>
              <Select value={selContract} onValueChange={setSelContract}>
                <SelectTrigger data-testid={`contract-select-${kind}`}>
                  <SelectValue placeholder="Selecione o contrato…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {contracts.map((c) => (
                    <SelectItem key={c.contract} value={c.contract}>
                      {c.contract} ({c.count} funcionário{c.count > 1 ? "s" : ""})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <DialogFooter>
                <Button variant="outline" onClick={() => setContractOpen(false)}>
                  Cancelar
                </Button>
                <Button
                  onClick={startBulk}
                  disabled={!selContract || bulkStarting}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid={`contract-start-${kind}`}
                >
                  {bulkStarting
                    ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                    : <Building2 className="w-4 h-4 mr-1.5" />}
                  Emitir para{" "}
                  {contracts.find((c) => c.contract === selContract)?.count || ""}{" "}
                  funcionários
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3" data-testid={`contract-progress-${kind}`}>
              <div className="text-sm font-medium text-slate-800">
                {bulkJob.status === "running"
                  ? "Emitindo documentos…"
                  : bulkJob.status === "finished"
                    ? "Emissão concluída!"
                    : "Emissão com erro"}
              </div>
              <div className="w-full h-2.5 rounded-full bg-slate-100 overflow-hidden">
                <div
                  className="h-full bg-indigo-600 transition-all"
                  style={{
                    width: `${Math.min(100,
                      Math.round(((bulkJob.done + bulkJob.skipped +
                        (bulkJob.errors || 0)) / (bulkJob.total || 1)) * 100))}%`,
                  }}
                />
              </div>
              <div className="text-xs text-slate-600">
                {bulkJob.done + bulkJob.skipped + (bulkJob.errors || 0)} de{" "}
                {bulkJob.total} processados — {bulkJob.done} emitidos,{" "}
                {bulkJob.skipped} já tinham
                {bulkJob.errors ? `, ${bulkJob.errors} erro(s)` : ""}
              </div>
              {bulkJob.status !== "running" && (
                <DialogFooter>
                  <Button onClick={() => setContractOpen(false)} data-testid={`contract-close-${kind}`}>
                    Fechar
                  </Button>
                </DialogFooter>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Gerar {title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <label className="text-xs font-medium text-slate-700">Funcionário</label>
              <Input
                placeholder="Buscar funcionário…"
                value={empQuery}
                onChange={(e) => setEmpQuery(e.target.value)}
              />
              <Select value={selectedEmp} onValueChange={setSelectedEmp}>
                <SelectTrigger data-testid={`select-emp-${kind}`}>
                  <SelectValue placeholder="Selecione…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {employees
                    .filter((e) => {
                      if (!empQuery) return true;
                      const q = empQuery.toLowerCase();
                      return (e.nome || "").toLowerCase().includes(q)
                        || (e.matricula || "").includes(q);
                    })
                    .slice(0, 100)
                    .map((e) => (
                      <SelectItem key={e.id} value={e.id}>
                        {e.matricula} · {e.nome}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            {showCarga && (
              <div className="space-y-1.5">
                <label className="text-xs font-medium text-slate-700">
                  Carga Horária
                </label>
                <Input
                  value={carga}
                  onChange={(e) => setCarga(e.target.value)}
                  placeholder="ex: 6h, 8h"
                  data-testid={`carga-${kind}`}
                />
              </div>
            )}
            {showFuncaoPosto && (
              <>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">
                    Função (opcional)
                  </label>
                  <Input
                    value={funcao}
                    onChange={(e) => setFuncao(e.target.value)}
                    placeholder="Vigilante"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">
                    Posto (opcional)
                  </label>
                  <Input
                    value={posto}
                    onChange={(e) => setPosto(e.target.value)}
                    placeholder="SERPRO - SP01"
                  />
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleGenerate} disabled={submitting}
                    data-testid={`confirm-generate-${kind}`}>
              {submitting
                ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Gerando…</>
                : <>Gerar Documento</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkDocsUploadDialog
        open={bulkOpen}
        onOpenChange={setBulkOpen}
        kind={kind}
        adminToken={adminToken}
        title={`Importar ${title} em lote (Folhamatic)`}
        accentClassName="bg-blue-700 hover:bg-blue-800"
        onConfirmed={(r) => {
          toast.success(
            `${r.created || 0} documento(s) criado(s)` +
            (r.errors_count ? ` · ${r.errors_count} erro(s)` : ""),
          );
          load();
        }}
      />
    </div>
  );
};

export default AdminKitDocSimpleTab;
